import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { toast } from "sonner";
import { CalendarIcon, Download, CheckCircle, XCircle, Clock, FileCheck, Filter, Plus, Pencil, Loader2, Trash2, Globe } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../../components/ui/alert-dialog";
import { formatSydney, formatTimeSydney, formatTableDateSydney, toSydneyTime } from "../../utils/timezone";
import { format } from "date-fns";

const statusConfig = {
  draft: { label: "Draft", className: "badge-draft" },
  submitted: { label: "Submitted", className: "badge-submitted" },
  approved: { label: "Approved", className: "badge-approved" },
  rejected: { label: "Rejected", className: "badge-rejected" },
};

export default function AdminTimesheets() {
  const { api } = useAuth();
  const [timesheets, setTimesheets] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [serverTime, setServerTime] = useState(null);
  
  const [filters, setFilters] = useState({
    user_id: "all",
    status: "all",
    start_date: new Date(new Date().setDate(new Date().getDate() - 30)),
    end_date: new Date(),
  });

  const [form, setForm] = useState({
    user_id: "",
    date: new Date(),
    clock_in: "09:00",
    clock_out: "17:00",
    break_minutes: 30,
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [timesheetsRes, usersRes, timeRes] = await Promise.all([
        api().get("/admin/timesheets", {
          params: {
            user_id: filters.user_id === "all" ? undefined : filters.user_id,
            status: filters.status === "all" ? undefined : filters.status,
            start_date: format(filters.start_date, "yyyy-MM-dd"),
            end_date: format(filters.end_date, "yyyy-MM-dd"),
          },
        }),
        api().get("/admin/users"),
        api().get("/server-time"),
      ]);
      setTimesheets(timesheetsRes.data);
      setUsers(usersRes.data.filter(u => u.role === "staff"));
      setServerTime(timeRes.data);
    } catch (error) {
      toast.error("Failed to load timesheets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filters]);

  const resetForm = () => {
    setForm({
      user_id: "",
      date: new Date(),
      clock_in: "09:00",
      clock_out: "17:00",
      break_minutes: 30,
    });
    setEditingId(null);
  };

  const handleApprove = async (entryId) => {
    try {
      await api().put(`/admin/timesheets/${entryId}/approve`);
      toast.success("Timesheet approved!");
      fetchData();
    } catch (error) {
      toast.error("Failed to approve timesheet");
    }
  };

  const handleReject = async (entryId) => {
    try {
      await api().put(`/admin/timesheets/${entryId}/reject`);
      toast.success("Timesheet rejected");
      fetchData();
    } catch (error) {
      toast.error("Failed to reject timesheet");
    }
  };

  const handleSubmitEntry = async (e) => {
    e.preventDefault();
    if (!form.user_id) {
      toast.error("Please select a staff member");
      return;
    }

    setSaving(true);
    try {
      const dateStr = format(form.date, "yyyy-MM-dd");
      const clockInTime = new Date(`${dateStr}T${form.clock_in}:00`);
      const clockOutTime = new Date(`${dateStr}T${form.clock_out}:00`);
      const totalMinutes = (clockOutTime - clockInTime) / 60000;
      const totalHours = Math.max(0, (totalMinutes - form.break_minutes) / 60);

      if (editingId) {
        await api().put(`/admin/timesheets/${editingId}`, {
          clock_in: clockInTime.toISOString(),
          clock_out: clockOutTime.toISOString(),
          break_minutes: parseInt(form.break_minutes) || 0,
          total_hours: parseFloat(totalHours.toFixed(2)),
        });
        toast.success("Timesheet updated!");
      } else {
        await api().post("/admin/timesheets", {
          user_id: form.user_id,
          date: dateStr,
          clock_in: clockInTime.toISOString(),
          clock_out: clockOutTime.toISOString(),
          break_minutes: parseInt(form.break_minutes) || 0,
          total_hours: parseFloat(totalHours.toFixed(2)),
        });
        toast.success("Timesheet entry created!");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save timesheet");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (entry) => {
    const clockIn = entry.clock_in ? new Date(entry.clock_in) : null;
    const clockOut = entry.clock_out ? new Date(entry.clock_out) : null;
    
    setForm({
      user_id: entry.user_id,
      date: new Date(entry.date),
      clock_in: clockIn ? format(clockIn, "HH:mm") : "09:00",
      clock_out: clockOut ? format(clockOut, "HH:mm") : "17:00",
      break_minutes: entry.break_minutes || 0,
    });
    setEditingId(entry.id);
    setDialogOpen(true);
  };

  const handleDelete = async (entryId) => {
    try {
      await api().delete(`/admin/timesheets/${entryId}`);
      toast.success("Timesheet deleted!");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete timesheet");
    }
  };

  const handleExportPayroll = async () => {
    setExporting(true);
    try {
      const response = await api().get("/admin/export/payroll", {
        params: {
          start_date: format(filters.start_date, "yyyy-MM-dd"),
          end_date: format(filters.end_date, "yyyy-MM-dd"),
        },
        responseType: "blob",
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `payroll_${format(filters.start_date, "yyyy-MM-dd")}_${format(filters.end_date, "yyyy-MM-dd")}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Payroll exported!");
    } catch (error) {
      toast.error("Failed to export payroll");
    } finally {
      setExporting(false);
    }
  };

  // Calculate totals
  const totalHours = timesheets.reduce((sum, t) => sum + (t.total_hours || 0), 0);
  const pendingCount = timesheets.filter(t => t.status === "submitted").length;
  const approvedCount = timesheets.filter(t => t.status === "approved").length;

  return (
    <div className="space-y-8" data-testid="admin-timesheets">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Timesheet Management</h1>
          <p className="text-slate-500 mt-1">Review, approve, edit and add staff timesheets</p>
          {serverTime && (
            <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
              <Globe className="h-3 w-3" />
              Sydney Time: {serverTime.display}
            </p>
          )}
        </div>
        
        <div className="flex gap-2">
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-timesheet-btn">
                <Plus className="mr-2 h-4 w-4" />
                Add Entry
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{editingId ? "Edit Timesheet" : "Add Timesheet Entry"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmitEntry} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Staff Member *</Label>
                  <Select
                    value={form.user_id}
                    onValueChange={(value) => setForm({ ...form, user_id: value })}
                    disabled={!!editingId}
                  >
                    <SelectTrigger data-testid="timesheet-staff-select">
                      <SelectValue placeholder="Select staff" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                        disabled={!!editingId}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(form.date, "PPP")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={form.date}
                        onSelect={(date) => date && setForm({ ...form, date })}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="clock_in">Clock In</Label>
                    <Input
                      id="clock_in"
                      type="time"
                      value={form.clock_in}
                      onChange={(e) => setForm({ ...form, clock_in: e.target.value })}
                      data-testid="timesheet-clock-in"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="clock_out">Clock Out</Label>
                    <Input
                      id="clock_out"
                      type="time"
                      value={form.clock_out}
                      onChange={(e) => setForm({ ...form, clock_out: e.target.value })}
                      data-testid="timesheet-clock-out"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="break">Break Minutes</Label>
                  <Input
                    id="break"
                    type="number"
                    min="0"
                    value={form.break_minutes}
                    onChange={(e) => setForm({ ...form, break_minutes: e.target.value })}
                    data-testid="timesheet-break"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={saving}
                  data-testid="timesheet-submit-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : editingId ? "Update Entry" : "Add Entry"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          
          <Button
            onClick={handleExportPayroll}
            disabled={exporting}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="export-payroll-btn"
          >
            <Download className="mr-2 h-4 w-4" />
            {exporting ? "Exporting..." : "Export Payroll"}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Staff Member</Label>
              <Select
                value={filters.user_id}
                onValueChange={(value) => setFilters({ ...filters, user_id: value })}
              >
                <SelectTrigger data-testid="filter-staff-select">
                  <SelectValue placeholder="All Staff" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Staff</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={filters.status}
                onValueChange={(value) => setFilters({ ...filters, status: value })}
              >
                <SelectTrigger data-testid="filter-status-select">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="submitted">Submitted</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                    data-testid="filter-start-date"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(filters.start_date, "MMM dd")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.start_date}
                    onSelect={(date) => date && setFilters({ ...filters, start_date: date })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>End Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                    data-testid="filter-end-date"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(filters.end_date, "MMM dd")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.end_date}
                    onSelect={(date) => date && setFilters({ ...filters, end_date: date })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <Clock className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 font-mono">{totalHours.toFixed(1)}h</p>
                <p className="text-sm text-slate-500">Total Hours</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-lg">
                <FileCheck className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-800 font-mono">{pendingCount}</p>
                <p className="text-sm text-amber-700">Pending Review</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-50 rounded-lg">
                <CheckCircle className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 font-mono">{approvedCount}</p>
                <p className="text-sm text-slate-500">Approved</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Timesheets Table */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg">Timesheet Entries</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner w-8 h-8" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead>
                  <tr>
                    <th>Staff</th>
                    <th>Date</th>
                    <th>Clock In</th>
                    <th>Clock Out</th>
                    <th>Break</th>
                    <th>Hours</th>
                    <th>Rate</th>
                    <th>Pay</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {timesheets.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="text-center py-8 text-slate-500">
                        No timesheet entries found
                      </td>
                    </tr>
                  ) : (
                    timesheets.map((entry) => {
                      const status = statusConfig[entry.status];
                      const pay = (entry.total_hours || 0) * (entry.hourly_rate || 0);
                      return (
                        <tr key={entry.id} data-testid={`timesheet-row-${entry.id}`}>
                          <td className="font-medium">{entry.user_name}</td>
                          <td>{formatTableDateSydney(entry.date)}</td>
                          <td>{formatTimeSydney(entry.clock_in)}</td>
                          <td>{entry.clock_out ? formatTimeSydney(entry.clock_out) : "-"}</td>
                          <td>{entry.break_minutes}m</td>
                          <td className="font-mono">{entry.total_hours?.toFixed(2)}h</td>
                          <td className="font-mono">${entry.hourly_rate?.toFixed(2)}</td>
                          <td className="font-mono font-medium">${pay.toFixed(2)}</td>
                          <td>
                            <Badge className={status.className}>{status.label}</Badge>
                          </td>
                          <td>
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleEdit(entry)}
                                data-testid={`edit-timesheet-${entry.id}`}
                              >
                                <Pencil className="h-4 w-4 text-blue-600" />
                              </Button>
                              {entry.status === "submitted" && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-emerald-600 hover:bg-emerald-50"
                                    onClick={() => handleApprove(entry.id)}
                                    data-testid={`approve-timesheet-${entry.id}`}
                                  >
                                    <CheckCircle className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-red-600 hover:bg-red-50"
                                    onClick={() => handleReject(entry.id)}
                                    data-testid={`reject-timesheet-${entry.id}`}
                                  >
                                    <XCircle className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-red-600 hover:bg-red-50"
                                    data-testid={`delete-timesheet-${entry.id}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Timesheet Entry?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will permanently delete the timesheet entry for {entry.user_name} on {formatTableDateSydney(entry.date)}. This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDelete(entry.id)}
                                      className="bg-red-600 hover:bg-red-700"
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
